import { useAccount } from '../contexts/AccountContext';
import { useBilling } from '../contexts/BillingContext';
import { getCombinedRemaining, isCombinedLimitReached } from '../utils/limitUtils';
import { getUsagePeriodKey } from '../utils/usageUtils';

const EMPTY_OBJECT: Record<string, never> = {};

export type LimitType = 'drafts' | 'reviews' | 'reports' | 'adHocDocs' | 'sectionChats';

export const useTeamLimits = () => {
  const { account } = useAccount();
  const { ledger } = useBilling();

  const checkLimit = (type: LimitType, usedPerTenderCount: number = 0) => {
    if (!account) {
      return {
        remaining: 0,
        isLimitReached: true,
        hasGenerationCapacity: false,
        isUnlimited: false,
      };
    }

    const isPayAsYouGo = ledger?.isPAYG ?? account.subscription?.billing_model === 'payg';

    if (isPayAsYouGo) {
      return {
        remaining: 'Unlimited',
        isLimitReached: false,
        hasGenerationCapacity: true,
        isUnlimited: true,
      };
    }

    let perTenderLimit: number | undefined | null;
    let monthlyLimit: number | undefined | null;
    let monthlyUsage = 0;

    const monthKey = getUsagePeriodKey(account.subscription?.current_period_end);
    const monthlyData = account.usage?.monthly?.[monthKey] || EMPTY_OBJECT;

    switch (type) {
      case 'drafts':
        perTenderLimit = account.subscription?.limits?.numDraftsPerTender;
        monthlyLimit = account.limits?.numDrafts ?? account.subscription?.limits?.numDrafts ?? 0;
        monthlyUsage = (monthlyData as Record<string, number>).drafts || 0;
        break;
      case 'reviews':
        perTenderLimit = account.subscription?.limits?.numReviewsPerTender;
        monthlyLimit = account.limits?.numReviews ?? account.subscription?.limits?.numReviews ?? 0;
        monthlyUsage = (monthlyData as Record<string, number>).reviews || 0;
        break;
      case 'reports':
        perTenderLimit = account.subscription?.limits?.numReportsPerTender;
        monthlyLimit = account.limits?.numReports ?? account.subscription?.limits?.numReports ?? 0;
        monthlyUsage = (monthlyData as Record<string, number>).reports || 0;
        break;
      case 'adHocDocs':
        perTenderLimit = account.subscription?.limits?.numAdHocDocsPerTender;
        monthlyLimit =
          account.limits?.numAdHocDocs ?? account.subscription?.limits?.numAdHocDocs ?? 0;
        monthlyUsage =
          ((monthlyData as Record<string, number>).adhoc_documents || 0) +
          ((monthlyData as Record<string, number>).generated_cvs || 0);
        break;
      case 'sectionChats':
        perTenderLimit = account.subscription?.limits?.numSectionChatsPerTender;
        monthlyLimit =
          account.limits?.numSectionChats ?? account.subscription?.limits?.numSectionChats ?? 0;
        monthlyUsage = (monthlyData as Record<string, number>).section_chats || 0;
        break;
    }

    const limitsWithUsage: { limit: number | undefined | null; usage: number }[] = [];
    if (perTenderLimit !== undefined) {
      limitsWithUsage.push({ limit: perTenderLimit, usage: usedPerTenderCount });
    }
    if (monthlyLimit !== undefined) {
      limitsWithUsage.push({ limit: monthlyLimit, usage: monthlyUsage });
    }

    const remaining = getCombinedRemaining(limitsWithUsage);
    const isLimitReached = isCombinedLimitReached(limitsWithUsage);

    return {
      remaining,
      isLimitReached,
      hasGenerationCapacity: !isLimitReached,
      isUnlimited: remaining === 'Unlimited',
    };
  };

  return { checkLimit };
};
